import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { rupiah, monthLabel, PAYMENT_STATUS } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Loader2, Users, Wallet, CalendarClock, AlertTriangle, Clock, GraduationCap } from "lucide-react";
import { TrendCharts } from "@/components/TrendCharts";
import { TopAttendance } from "@/components/TopAttendance";

function StatCard({ icon: Icon, label, value, sub, tint }) {
  return (
    <Card className="p-5 rounded-2xl border-amber-100 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-sm text-slate-500 font-medium">{label}</div>
          <div className="font-display text-3xl font-bold text-slate-900 mt-1">{value}</div>
          {sub && <div className="text-xs text-slate-400 mt-1">{sub}</div>}
        </div>
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${tint}`}><Icon className="w-6 h-6" /></div>
      </div>
    </Card>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data));
  }, []);

  if (!stats) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-orange-500" /></div>;

  const pct = stats.target > 0 ? Math.round((stats.collected / stats.target) * 100) : 0;

  return (
    <div className="space-y-6 animate-float-in">
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900">Halo, selamat datang! 👋</h1>
        <p className="text-slate-500 mt-1">Ringkasan Bimbel Gemma untuk {monthLabel(stats.month)}.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard icon={Users} label="Siswa Aktif" value={stats.total_students}
          sub={`TPA ${stats.by_level.TPA} · TK ${stats.by_level.TK} · SD ${stats.by_level.SD}`}
          tint="bg-orange-100 text-orange-600" />
        <StatCard icon={Wallet} label="Iuran Terkumpul" value={rupiah(stats.collected)}
          sub={`dari target ${rupiah(stats.target)}`} tint="bg-emerald-100 text-emerald-600" />
        <StatCard icon={AlertTriangle} label="Belum Lunas" value={stats.unpaid_count}
          sub="siswa perlu ditagih" tint="bg-rose-100 text-rose-600" />
        <StatCard icon={CalendarClock} label={`Kelas Hari ${stats.today_name}`} value={stats.today_classes.length}
          sub="pertemuan terjadwal" tint="bg-sky-100 text-sky-600" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Payment progress */}
        <Card className="p-6 rounded-2xl border-amber-100 lg:col-span-2">
          <h3 className="font-display text-xl font-bold text-slate-900 mb-4">Rekap Iuran {monthLabel(stats.month)}</h3>
          <div className="flex items-end justify-between mb-2">
            <span className="text-sm text-slate-500">Terkumpul</span>
            <span className="font-bold text-slate-900">{pct}%</span>
          </div>
          <div className="w-full h-4 bg-orange-100 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-orange-500 to-amber-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6">
            {Object.entries(stats.status_count).map(([k, v]) => (
              <div key={k} className={`px-3 py-3 rounded-xl border text-center ${PAYMENT_STATUS[k]?.cls || ""}`}>
                <div className="font-display text-2xl font-bold">{v}</div>
                <div className="text-xs font-semibold">{PAYMENT_STATUS[k]?.label || k}</div>
              </div>
            ))}
          </div>
        </Card>

        {/* Today classes */}
        <Card className="p-6 rounded-2xl border-amber-100">
          <div className="flex items-center gap-2 mb-4">
            <Clock className="w-5 h-5 text-sky-600" />
            <h3 className="font-display text-xl font-bold text-slate-900">Jadwal Hari Ini</h3>
          </div>
          {stats.today_classes.length === 0 ? (
            <p className="text-sm text-slate-400 py-6 text-center">Tidak ada kelas hari ini 🌙</p>
          ) : (
            <div className="space-y-2 max-h-72 overflow-y-auto gemma-scroll">
              {stats.today_classes.map((c, i) => (
                <button key={i} data-testid={`today-class-${i}`} onClick={() => navigate(`/siswa/${c.student_id}`)}
                  className="w-full flex items-center gap-3 p-3 rounded-xl bg-amber-50 hover:bg-amber-100 transition-colors text-left">
                  <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center shrink-0">
                    <GraduationCap className="w-5 h-5 text-orange-500" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-slate-800 truncate">{c.name}</div>
                    <div className="text-xs text-slate-400">{c.level}</div>
                  </div>
                  <div className="text-sm font-bold text-orange-600">{c.time}</div>
                </button>
              ))}
            </div>
          )}
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2"><TrendCharts /></div>
        <TopAttendance />
      </div>
    </div>
  );
}
